import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, ChevronDown, ChevronRight, ClipboardList, Download, Eye, Pencil, Plus, RefreshCw, Shield, ShoppingBag, Trash2, UserCog, Users, X } from 'lucide-react';
import toast from 'react-hot-toast';

import { authAPI } from '../../api/auth';
import { eventsAPI } from '../../api/events';
import { programsAPI } from '../../api/programs';
import { shopAPI } from '../../api/shop';
import { useAuth } from '../../contexts/AuthContext';
import ParticipantsModal from '../shared/ParticipantsModal';

const orderStatuses = [
  { value: 'pending', label: 'Ожидает обработки' },
  { value: 'processing', label: 'В обработке' },
  { value: 'delivered', label: 'Доставлен' },
  { value: 'completed', label: 'Завершен' },
  { value: 'cancelled', label: 'Отменен' },
];
const ORDER_FILTERS = [
  { value: 'open', label: 'Активные' },
  { value: 'closed', label: 'Закрытые' },
  { value: 'all', label: 'Все' },
];
const CLOSED_ORDER_STATUSES = new Set(['completed', 'cancelled']);

const weekDaysOrder = ['Воскресенье', 'Понедельник', 'Вторник', 'Среда', 'Четверг', 'Пятница', 'Суббота'];
const weekDaysShort = {
  'Воскресенье': 'Вс',
  'Понедельник': 'Пн',
  'Вторник': 'Вт',
  'Среда': 'Ср',
  'Четверг': 'Чт',
  'Пятница': 'Пт',
  'Суббота': 'Сб',
};

const getTodayWeekdayRu = () => weekDaysOrder[new Date().getDay()];
const getCurrentWeekStartISO = () => {
  const now = new Date();
  const day = now.getDay();
  const diff = day; // Sunday as start
  const start = new Date(now);
  start.setDate(now.getDate() - diff);
  const y = start.getFullYear();
  const m = String(start.getMonth() + 1).padStart(2, '0');
  const d = String(start.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const emptyTrainingForm = {
  schedule_type: 'recurring',
  week_start: '',
  sort_order: 0,
  is_published: true,
  is_new: false,
  icon: '',
  weekday: '',
  time: '',
  title: '',
  location: '',
  details: '',
  note: '',
  meeting_point_label: 'Точка сбора',
  meeting_point_url: '',
};

const trainingScheduleTypeOptions = [
  {
    value: 'recurring',
    label: 'Каждую неделю',
    description: 'Основной режим. Атлеты видят такую тренировку постоянно по выбранному дню недели.',
  },
  {
    value: 'specific_week',
    label: 'Только конкретная неделя',
    description: 'Используйте для разовой замены или особого расписания на одну неделю.',
  },
];

const getTrainingScheduleMeta = (training) => {
  if (training?.schedule_type === 'specific_week') {
    return {
      label: 'Конкретная неделя',
      description: training?.week_start ? `Неделя от ${training.week_start}` : 'Неделя не указана',
    };
  }

  return {
    label: 'Каждую неделю',
    description: 'Показывается атлетам постоянно по дню недели',
  };
};

const AdminDashboardTab = () => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [groupTrainings, setGroupTrainings] = useState([]);
  const [eventFeedbacks, setEventFeedbacks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [userSearch, setUserSearch] = useState('');
  const [selectedTrainingDay, setSelectedTrainingDay] = useState('');

  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [trainingForm, setTrainingForm] = useState(emptyTrainingForm);
  const [editingTrainingId, setEditingTrainingId] = useState(null);

  const [showOrderModal, setShowOrderModal] = useState(false);
  const [orderModalData, setOrderModalData] = useState(null);
  const [orderModalStatus, setOrderModalStatus] = useState('pending');
  const [activeSection, setActiveSection] = useState('users');
  const [orderFilter, setOrderFilter] = useState('open');
  const [collapsedSections, setCollapsedSections] = useState({
    users: false,
    orders: false,
    trainings: false,
    feedbacks: false,
  });
  const [participantsModalData, setParticipantsModalData] = useState({
    isOpen: false,
    title: '',
    participants: [],
    totalCount: 0,
  });

  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    description: '',
    actionType: null,
    payload: null,
  });
  const [actionProcessing, setActionProcessing] = useState(false);
  const [showUserProfileModal, setShowUserProfileModal] = useState(false);
  const [selectedAdminUser, setSelectedAdminUser] = useState(null);
  const [userProfileLoading, setUserProfileLoading] = useState(false);
  const todayWeekday = getTodayWeekdayRu();
  const adminDisplayName = useMemo(() => {
    const fullName = `${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim();
    return fullName || currentUser?.username || 'Администратор';
  }, [currentUser?.first_name, currentUser?.last_name, currentUser?.username]);
  const adminEmail = currentUser?.email || 'email не указан';
  const adminRole = currentUser?.is_superuser ? 'Superuser' : 'Администратор';
  const filteredOrders = useMemo(() => {
    if (orderFilter === 'closed') {
      return orders.filter((order) => CLOSED_ORDER_STATUSES.has(order.status));
    }
    if (orderFilter === 'all') {
      return orders;
    }
    return orders.filter((order) => !CLOSED_ORDER_STATUSES.has(order.status));
  }, [orders, orderFilter]);

  const loadAdminData = async () => {
    try {
      setIsLoading(true);
      const [usersData, ordersData, trainingsData, feedbacksData] = await Promise.all([
        authAPI.getAdminUsers(),
        shopAPI.getOrders({ adminAll: true }),
        programsAPI.getGroupTrainings(),
        eventsAPI.getAdminEventFeedbacks(),
      ]);

      const usersList = Array.isArray(usersData) ? usersData : [];
      const ordersList = Array.isArray(ordersData) ? ordersData : (ordersData?.results || []);
      const trainingsList = Array.isArray(trainingsData) ? trainingsData : (trainingsData?.results || []);
      const feedbacksList = Array.isArray(feedbacksData) ? feedbacksData : (feedbacksData?.results || []);

      setUsers(usersList);
      setOrders(ordersList);
      setGroupTrainings(trainingsList);
      setEventFeedbacks(feedbacksList);
    } catch (error) {
      console.error('Error loading admin data:', error);
      toast.error('Не удалось загрузить данные админ-панели');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadAdminData();
  }, []);

  useEffect(() => {
    if (!groupTrainings.length) {
      setSelectedTrainingDay(todayWeekday);
      return;
    }
    const todayTrainings = groupTrainings.filter((item) => item.weekday === todayWeekday);
    if (todayTrainings.length > 0) {
      setSelectedTrainingDay(todayWeekday);
      return;
    }

    const firstAvailableDay = weekDaysOrder.find((day) =>
      groupTrainings.some((item) => item.weekday === day),
    );
    setSelectedTrainingDay(firstAvailableDay || todayWeekday);
  }, [groupTrainings, todayWeekday]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) => {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim().toLowerCase();
      return (
        String(user.username || '').toLowerCase().includes(q)
        || String(user.email || '').toLowerCase().includes(q)
        || fullName.includes(q)
      );
    });
  }, [users, userSearch]);

  const groupedTrainings = useMemo(() => {
    const groups = weekDaysOrder.reduce((acc, day) => {
      acc[day] = [];
      return acc;
    }, {});

    groupTrainings.forEach((item) => {
      if (!groups[item.weekday]) {
        groups[item.weekday] = [];
      }
      groups[item.weekday].push(item);
    });

    Object.keys(groups).forEach((day) => {
      groups[day].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));
    });

    return groups;
  }, [groupTrainings]);

  const selectedDayTrainings = groupedTrainings[selectedTrainingDay] || [];
  const feedbackStats = useMemo(() => {
    if (!eventFeedbacks.length) {
      return {
        total: 0,
        approved: 0,
        pending: 0,
        achievedRate: 0,
        avgCompetition: 0,
        avgPreparation: 0,
      };
    }
    const total = eventFeedbacks.length;
    const approved = eventFeedbacks.filter((f) => f.status === 'approved').length;
    const pending = eventFeedbacks.filter((f) => f.status === 'pending').length;
    const achievedCount = eventFeedbacks.filter((f) => f.achieved_goals).length;
    const avgCompetition = eventFeedbacks.reduce((sum, f) => sum + Number(f.competition_rating || 0), 0) / total;
    const avgPreparation = eventFeedbacks.reduce((sum, f) => sum + Number(f.preparation_rating || 0), 0) / total;
    return {
      total,
      approved,
      pending,
      achievedRate: Math.round((achievedCount / total) * 100),
      avgCompetition: avgCompetition.toFixed(2),
      avgPreparation: avgPreparation.toFixed(2),
    };
  }, [eventFeedbacks]);
  const adminMenuItems = useMemo(() => ([
    { id: 'users', label: 'Пользователи', count: filteredUsers.length },
    { id: 'orders', label: 'Заказы', count: orders.length },
    { id: 'trainings', label: 'Тренировки', count: groupTrainings.length },
    { id: 'feedbacks', label: 'Отзывы и цели', count: eventFeedbacks.length },
  ]), [filteredUsers.length, orders.length, groupTrainings.length, eventFeedbacks.length]);

  const toggleSection = (sectionKey) => {
    setCollapsedSections((prev) => ({
      ...prev,
      [sectionKey]: !prev[sectionKey],
    }));
  };

  const openConfirm = ({ title, description, actionType, payload }) => {
    setConfirmModal({
      isOpen: true,
      title,
      description,
      actionType,
      payload,
    });
  };

  const closeConfirm = () => {
    setConfirmModal({
      isOpen: false,
      title: '',
      description: '',
      actionType: null,
      payload: null,
    });
  };

  const requestPatchUser = (userId, payload, label) => {
    openConfirm({
      title: 'Подтвердить изменение',
      description: `Изменить пользователя: ${label}?`,
      actionType: 'user_patch',
      payload: { userId, patch: payload },
    });
  };

  const requestDeleteUser = (targetUser) => {
    const userLabel = targetUser?.first_name || targetUser?.last_name
      ? `${targetUser.first_name || ''} ${targetUser.last_name || ''}`.trim()
      : `@${targetUser.username}`;
    const confirmed = window.confirm(`Удалить пользователя ${userLabel}?`);
    if (!confirmed) return;

    const deleteAllData = window.confirm(
      'Удалить все данные пользователя?\nOK = Да, удалить все\nОтмена = Нет, сохранить данные (анонимизировать аккаунт)'
    );
    const deleteReviews = window.confirm(
      'Удалить отзывы пользователя?\nOK = Да, удалить отзывы\nОтмена = Нет, оставить отзывы с именем автора'
    );

    const summary = `Удаление пользователя ${userLabel}. Режим: ${deleteAllData ? 'полное удаление' : 'анонимизация'}. Отзывы: ${deleteReviews ? 'удалить' : 'сохранить с именем'}.`;
    openConfirm({
      title: 'Подтвердить удаление пользователя',
      description: summary,
      actionType: 'user_delete',
      payload: {
        userId: targetUser.id,
        options: {
          delete_all_data: deleteAllData,
          delete_reviews: deleteReviews,
        },
      },
    });
  };

  const openUserProfile = async (targetUser) => {
    try {
      setUserProfileLoading(true);
      setShowUserProfileModal(true);
      const detail = await authAPI.getAdminUserDetail(targetUser.id);
      setSelectedAdminUser(detail);
    } catch (error) {
      console.error('Error loading admin user detail:', error);
      toast.error('Не удалось загрузить профиль атлета');
      setShowUserProfileModal(false);
      setSelectedAdminUser(null);
    } finally {
      setUserProfileLoading(false);
    }
  };

  const openCreateTraining = () => {
    setEditingTrainingId(null);
    const day = selectedTrainingDay || todayWeekday;
    setTrainingForm({
      ...emptyTrainingForm,
      schedule_type: 'recurring',
      week_start: getCurrentWeekStartISO(),
      weekday: day,
    });
    setShowTrainingModal(true);
  };

  const openEditTraining = (training) => {
    setEditingTrainingId(training.id);
    setTrainingForm({
      schedule_type: training.schedule_type || 'recurring',
      week_start: training.week_start || '',
      sort_order: training.sort_order ?? 0,
      is_published: Boolean(training.is_published),
      is_new: Boolean(training.is_new),
      icon: training.icon || '',
      weekday: training.weekday || '',
      time: training.time || '',
      title: training.title || '',
      location: training.location || '',
      details: training.details || '',
      note: training.note || '',
      meeting_point_label: training.meeting_point_label || 'Точка сбора',
      meeting_point_url: training.meeting_point_url || '',
    });
    setShowTrainingModal(true);
  };

  const requestSaveTraining = () => {
    if (!trainingForm.weekday || !trainingForm.time || !trainingForm.title) {
      toast.error('Заполните обязательные поля: день, время, заголовок');
      return;
    }

    openConfirm({
      title: editingTrainingId ? 'Подтвердить изменение' : 'Подтвердить добавление',
      description: editingTrainingId
        ? 'Сохранить изменения групповой тренировки?'
        : 'Добавить новую групповую тренировку?',
      actionType: 'training_save',
      payload: null,
    });
  };

  const requestDeleteTraining = (trainingId) => {
    openConfirm({
      title: 'Подтвердить удаление',
      description: 'Удалить групповую тренировку? Это действие нельзя отменить.',
      actionType: 'training_delete',
      payload: { trainingId },
    });
  };

  const openOrder = async (order) => {
    try {
      const fullOrder = await shopAPI.getOrder(order.id);
      setOrderModalData(fullOrder);
      setOrderModalStatus(fullOrder.status || 'pending');
      setShowOrderModal(true);
    } catch (error) {
      console.error('Error loading order:', error);
      setOrderModalData(order);
      setOrderModalStatus(order.status || 'pending');
      setShowOrderModal(true);
    }
  };

  const requestOrderStatusUpdate = () => {
    if (!orderModalData) return;
    if (orderModalStatus === orderModalData.status) {
      toast('Статус не изменен');
      return;
    }

    openConfirm({
      title: 'Подтвердить изменение статуса',
      description: `Изменить статус заказа #${orderModalData.id}?`,
      actionType: 'order_status',
      payload: { orderId: orderModalData.id, status: orderModalStatus },
    });
  };

  const confirmAction = async () => {
    const { actionType, payload } = confirmModal;
    closeConfirm();

    try {
      setActionProcessing(true);

      if (actionType === 'user_patch' && payload) {
        const updated = await authAPI.updateAdminUser(payload.userId, payload.patch);
        setUsers((prev) => prev.map((item) => (item.id === payload.userId ? updated : item)));
        toast.success('Пользователь обновлен');
        return;
      }

      if (actionType === 'user_delete' && payload?.userId) {
        const result = await authAPI.deleteAdminUser(payload.userId, payload.options || {});
        if (result?.result === 'deleted') {
          setUsers((prev) => prev.filter((item) => item.id !== payload.userId));
          toast.success('Пользователь удален');
        } else {
          setUsers((prev) => prev.map((item) => (item.id === payload.userId ? result?.user || item : item)));
          toast.success('Аккаунт анонимизирован и отключен');
        }
        return;
      }

      if (actionType === 'training_save') {
        const submitPayload = {
          ...trainingForm,
          week_start: trainingForm.schedule_type === 'specific_week'
            ? (trainingForm.week_start || getCurrentWeekStartISO())
            : getCurrentWeekStartISO(),
          sort_order: Number(trainingForm.sort_order || 0),
        };
        if (editingTrainingId) {
          const updated = await programsAPI.updateGroupTraining(editingTrainingId, submitPayload);
          setGroupTrainings((prev) => prev.map((item) => (item.id === editingTrainingId ? updated : item)));
          toast.success('Тренировка обновлена');
        } else {
          const created = await programsAPI.createGroupTraining(submitPayload);
          setGroupTrainings((prev) => [created, ...prev]);
          toast.success('Тренировка добавлена');
        }
        setShowTrainingModal(false);
        setTrainingForm(emptyTrainingForm);
        setEditingTrainingId(null);
        await loadAdminData();
        return;
      }

      if (actionType === 'training_delete' && payload?.trainingId) {
        await programsAPI.deleteGroupTraining(payload.trainingId);
        setGroupTrainings((prev) => prev.filter((item) => item.id !== payload.trainingId));
        toast.success('Тренировка удалена');
        return;
      }

      if (actionType === 'order_status' && payload) {
        const updatedOrder = await shopAPI.updateOrder(payload.orderId, { status: payload.status });
        setOrders((prev) => prev.map((order) => (order.id === payload.orderId ? updatedOrder : order)));
        setOrderModalData((prev) => (prev ? { ...prev, ...updatedOrder } : prev));
        toast.success('Статус заказа обновлен');
        return;
      }

      if (actionType === 'feedback_approve' && payload?.feedbackId) {
        await eventsAPI.approveEventFeedback(payload.feedbackId);
        setEventFeedbacks((prev) => prev.map((item) => (
          item.id === payload.feedbackId ? { ...item, status: 'approved' } : item
        )));
        toast.success('Отзыв одобрен и опубликован');
        return;
      }

      if (actionType === 'feedback_reject' && payload?.feedbackId) {
        await eventsAPI.rejectEventFeedback(payload.feedbackId);
        setEventFeedbacks((prev) => prev.map((item) => (
          item.id === payload.feedbackId ? { ...item, status: 'rejected' } : item
        )));
        toast.success('Отзыв отклонен');
      }
    } catch (error) {
      console.error('Action error:', error);
      toast.error('Не удалось выполнить действие');
    } finally {
      setActionProcessing(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 flex justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#4897d2]" />
      </div>
    );
  }

  return (
    <div className="grid xl:grid-cols-[280px_minmax(0,1fr)] gap-6 items-start">
      <aside className="xl:sticky xl:top-24 space-y-4">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-5 border-b border-gray-200 bg-gradient-to-r from-[#662d91]/10 via-[#4897d2]/10 to-[#28c4f4]/10">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-full bg-[#4897d2]/15 text-[#2f77b2] flex items-center justify-center text-lg font-bold">
                {String(adminDisplayName || 'A').charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-[0.16em] text-gray-500">Профиль</p>
                <p className="text-base font-semibold text-gray-900 truncate">{adminDisplayName}</p>
                <p className="text-xs text-gray-600 truncate">{adminEmail}</p>
                <p className="text-xs text-[#2f77b2] mt-1">{adminRole}</p>
              </div>
            </div>
          </div>
          <div className="px-3 py-3 space-y-1">
            {adminMenuItems.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setActiveSection(item.id)}
                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center justify-between gap-2 ${
                  activeSection === item.id
                    ? 'bg-gradient-to-r from-[#662d91]/80 via-[#4897d2]/80 to-[#28c4f4]/80 text-white'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <span>{item.label}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${activeSection === item.id ? 'bg-white/30 text-white' : 'bg-gray-100 text-gray-600'}`}>{item.count}</span>
              </button>
            ))}
          </div>
        </div>
      </aside>

      <div className="space-y-6">
        <div id="admin-overview" className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="w-6 h-6 text-[#4897d2]" />
            Админ-панель
          </h2>
          <button
            type="button"
            onClick={loadAdminData}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700"
          >
            <RefreshCw className="w-4 h-4" />
            Обновить
          </button>
        </div>

        {activeSection === 'users' && (
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b bg-gradient-to-r from-[#662d91]/10 via-[#4897d2]/10 to-[#28c4f4]/10 flex items-start justify-between gap-3">
            <div className="flex-1">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <UserCog className="w-5 h-5 text-[#4897d2]" />
              Пользователи ({filteredUsers.length})
            </h3>
            <input
              type="text"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Поиск по имени, email, username"
              className="mt-3 w-full px-3 py-2 border rounded-lg text-sm"
            />
            </div>
            <button
              type="button"
              onClick={() => toggleSection('users')}
              title={collapsedSections.users ? 'Развернуть блок' : 'Свернуть блок'}
              aria-label={collapsedSections.users ? 'Развернуть блок пользователей' : 'Свернуть блок пользователей'}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/80 border border-gray-200 text-gray-700 hover:bg-white"
            >
              {collapsedSections.users ? <ChevronRight className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
          {!collapsedSections.users && (
            <div className="max-h-[420px] overflow-y-auto divide-y">
              {filteredUsers.map((user) => (
                <div key={user.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {user.first_name || user.last_name
                          ? `${user.first_name || ''} ${user.last_name || ''}`.trim()
                          : user.username}
                      </p>
                      <p className="text-sm text-gray-600">@{user.username}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                    <div className="text-right text-xs text-gray-500">
                      <div>ID: {user.id}</div>
                      <div>{user.is_superuser ? 'Superuser' : user.is_staff ? 'Admin' : 'User'}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => openUserProfile(user)}
                      className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
                    >
                      <Eye className="w-3.5 h-3.5" />
                      Профиль
                    </button>
                    <button
                      type="button"
                      onClick={() => requestPatchUser(user.id, { is_active: !user.is_active }, user.is_active ? 'отключить пользователя' : 'активировать пользователя')}
                      className={`px-3 py-1 rounded-full text-xs font-medium ${user.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-700'}`}
                    >
                      {user.is_active ? 'Активен' : 'Отключен'}
                    </button>
                    {!user.is_superuser && (
                      <button
                        type="button"
                        onClick={() => requestPatchUser(user.id, { is_staff: !user.is_staff }, user.is_staff ? 'снять права admin' : 'выдать права admin')}
                        className={`px-3 py-1 rounded-full text-xs font-medium ${user.is_staff ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-700'}`}
                      >
                        {user.is_staff ? 'Admin ON' : 'Admin OFF'}
                      </button>
                    )}
                    {!user.is_superuser && (
                      <button
                        type="button"
                        onClick={() => requestDeleteUser(user)}
                        className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700 hover:bg-red-200"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Удалить
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        )}

        {activeSection === 'orders' && (
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          <div className="px-5 py-4 border-b bg-gradient-to-r from-[#662d91]/10 via-[#4897d2]/10 to-[#28c4f4]/10 flex items-center justify-between gap-3">
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <ShoppingBag className="w-5 h-5 text-[#4897d2]" />
              Заказы ({filteredOrders.length})
            </h3>
            <button
              type="button"
              onClick={() => toggleSection('orders')}
              title={collapsedSections.orders ? 'Развернуть блок' : 'Свернуть блок'}
              aria-label={collapsedSections.orders ? 'Развернуть блок заказов' : 'Свернуть блок заказов'}
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/80 border border-gray-200 text-gray-700 hover:bg-white"
            >
              {collapsedSections.orders ? <ChevronRight className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </button>
          </div>
          {!collapsedSections.orders && (
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2 px-4 pt-4">
                {ORDER_FILTERS.map((filterItem) => (
                  <button
                    key={filterItem.value}
                    type="button"
                    onClick={() => setOrderFilter(filterItem.value)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      orderFilter === filterItem.value
                        ? 'bg-[#4897d2] text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {filterItem.label}
                  </button>
                ))}
              </div>
              <div className="max-h-[420px] overflow-y-auto divide-y">
              {filteredOrders.map((order) => (
                <div key={order.id} className="p-4">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900">Заказ #{order.id}</p>
                      <p className="text-sm text-gray-600">{order.user_email || 'Без email'}</p>
                      <p className="text-xs text-gray-500">{new Date(order.created_at).toLocaleString('ru-RU')}</p>
                    </div>
                    <p className="font-bold text-[#4897d2]">{parseFloat(order.total_price || 0).toFixed(2)}₪</p>
                  </div>
                  <div className="mt-3">
                    <button
                      type="button"
                      onClick={() => openOrder(order)}
                      className="px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm"
                    >
                      Открыть заказ
                    </button>
                  </div>
                </div>
              ))}
              {filteredOrders.length === 0 && (
                <div className="p-4 text-sm text-gray-500">В этом фильтре заказов пока нет.</div>
              )}
              </div>
            </div>
          )}
        </div>
        )}

      {activeSection === 'trainings' && (
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b bg-gradient-to-r from-[#662d91]/10 via-[#4897d2]/10 to-[#28c4f4]/10 flex items-center justify-between gap-3">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-[#4897d2]" />
            Групповые тренировки ({groupTrainings.length})
          </h3>
          <button
            type="button"
            onClick={() => toggleSection('trainings')}
            title={collapsedSections.trainings ? 'Развернуть блок' : 'Свернуть блок'}
            aria-label={collapsedSections.trainings ? 'Развернуть блок тренировок' : 'Свернуть блок тренировок'}
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/80 border border-gray-200 text-gray-700 hover:bg-white"
          >
            {collapsedSections.trainings ? <ChevronRight className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>

        {!collapsedSections.trainings && (
        <div className="p-4 sm:p-5 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
            {weekDaysOrder.map((day) => {
              const count = (groupedTrainings[day] || []).length;
              const isActive = selectedTrainingDay === day;
              const isToday = todayWeekday === day;
              return (
                <button
                  key={day}
                  type="button"
                  onClick={() => setSelectedTrainingDay(day)}
                  className={`text-left rounded-lg border px-3 py-2 transition-colors ${
                    isActive
                      ? 'border-[#4897d2] bg-[#4897d2]/10'
                      : 'border-gray-200 bg-white hover:bg-gray-50'
                  } ${isToday ? 'ring-1 ring-[#662d91]/40' : ''}`}
                >
                  <p className="text-xs sm:text-sm font-semibold text-gray-900">{weekDaysShort[day] || day}</p>
                  <p className="text-xs text-gray-500">{count} трен.{isToday ? ' • сегодня' : ''}</p>
                </button>
              );
            })}
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-gray-800">
                {selectedTrainingDay === todayWeekday ? 'Тренировки на сегодня' : `Тренировки: ${selectedTrainingDay}`}
              </p>
                <button
                  type="button"
                  onClick={openCreateTraining}
                  className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#4897d2] text-white hover:opacity-90 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Добавить в {weekDaysShort[selectedTrainingDay || todayWeekday] || selectedTrainingDay || todayWeekday}
                </button>
              </div>
            {selectedDayTrainings.length > 0 ? (
              selectedDayTrainings.map((training) => (
                <div key={training.id} className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-semibold text-gray-900">
                        {training.is_new ? '🆕 ' : ''}
                        {training.icon ? `${training.icon} ` : ''}
                        {training.weekday}, {training.time}, {training.title}
                      </p>
                      {training.location && <p className="text-sm text-gray-700 mt-1">{training.location}</p>}
                      {training.note && <p className="text-xs text-gray-500 mt-1 italic">{training.note}</p>}
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="inline-flex rounded-full bg-sky-100 px-2 py-1 text-xs font-medium text-sky-700">
                          {getTrainingScheduleMeta(training).label}
                        </span>
                        <span className="text-xs text-gray-500">{getTrainingScheduleMeta(training).description}</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Порядок: {training.sort_order}</p>
                      <p className="text-xs text-gray-600 mt-2 inline-flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        Участников: <span className="font-semibold">{training.participants_count || 0}</span>
                      </p>
                      <button
                        type="button"
                        onClick={() => setParticipantsModalData({
                          isOpen: true,
                          title: `Участники: ${training.weekday}, ${training.time}`,
                          participants: training.participants_preview || [],
                          totalCount: training.participants_count || 0,
                        })}
                        className="text-xs text-[#4897d2] hover:underline mt-1 ml-1"
                      >
                        Участники
                      </button>
                    </div>
                    <span className={`px-2 py-1 rounded-full text-xs ${training.is_published ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                      {training.is_published ? 'Публично' : 'Скрыто'}
                    </span>
                  </div>
                  <div className="mt-3 flex gap-2">
                    <button
                      type="button"
                      onClick={() => openEditTraining(training)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm"
                    >
                      <Pencil className="w-4 h-4" />
                      Изменить
                    </button>
                    <button
                      type="button"
                      onClick={() => requestDeleteTraining(training.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 text-sm"
                    >
                      <Trash2 className="w-4 h-4" />
                      Удалить
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="border border-dashed border-gray-300 rounded-xl p-5 text-sm text-gray-500 bg-gray-50">
                На выбранный день тренировок нет.
              </div>
            )}
          </div>
        </div>
        )}
      </div>
      )}

      {activeSection === 'feedbacks' && (
      <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b bg-gradient-to-r from-[#662d91]/10 via-[#4897d2]/10 to-[#28c4f4]/10 flex items-center justify-between gap-3">
          <h3 className="font-bold text-gray-900 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-[#4897d2]" />
            Цели атлетов и отзывы по событиям ({eventFeedbacks.length})
          </h3>
          <button
            type="button"
            onClick={() => toggleSection('feedbacks')}
            title={collapsedSections.feedbacks ? 'Развернуть блок' : 'Свернуть блок'}
            className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/80 border border-gray-200 text-gray-700 hover:bg-white"
          >
            {collapsedSections.feedbacks ? <ChevronRight className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>
        </div>
        {!collapsedSections.feedbacks && (
          <div className="max-h-[460px] overflow-auto">
            <div className="grid sm:grid-cols-3 gap-2 p-3 bg-gray-50 border-b">
              <div className="rounded-lg bg-white border border-gray-200 p-2 text-xs text-gray-700">
                Всего: <span className="font-semibold">{feedbackStats.total}</span><br />
                Pending: <span className="font-semibold">{feedbackStats.pending}</span><br />
                Approved: <span className="font-semibold">{feedbackStats.approved}</span>
              </div>
              <div className="rounded-lg bg-white border border-gray-200 p-2 text-xs text-gray-700">
                Достигли целей: <span className="font-semibold">{feedbackStats.achievedRate}%</span>
              </div>
              <div className="rounded-lg bg-white border border-gray-200 p-2 text-xs text-gray-700">
                Средние оценки:<br />
                Соревнование: <span className="font-semibold">{feedbackStats.avgCompetition}</span><br />
                Подготовка: <span className="font-semibold">{feedbackStats.avgPreparation}</span>
              </div>
            </div>
            {eventFeedbacks.length === 0 ? (
              <div className="p-4 text-sm text-gray-600">Пока нет отзывов по завершенным событиям.</div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2">Атлет</th>
                    <th className="text-left px-3 py-2">Событие</th>
                    <th className="text-left px-3 py-2">Цели достигнуты</th>
                    <th className="text-left px-3 py-2">Оценки</th>
                    <th className="text-left px-3 py-2">Выводы/отзыв</th>
                    <th className="text-left px-3 py-2">Goals Snapshot</th>
                    <th className="text-left px-3 py-2">Статус</th>
                    <th className="text-left px-3 py-2">Действия</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {eventFeedbacks.map((fb) => (
                    <tr key={fb.id}>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{fb.user_name || '-'}</div>
                        <div className="text-xs text-gray-500">{fb.user_email || '-'}</div>
                      </td>
                      <td className="px-3 py-2">
                        <div className="font-medium text-gray-900">{fb.event_title}</div>
                        <div className="text-xs text-gray-500">{fb.event_date ? new Date(fb.event_date).toLocaleDateString('ru-RU') : '-'}</div>
                      </td>
                      <td className="px-3 py-2">{fb.achieved_goals ? 'Да' : 'Нет'}</td>
                      <td className="px-3 py-2 text-xs text-gray-700">
                        Соревнование: {fb.competition_rating}/5
                        <br />
                        Подготовка: {fb.preparation_rating}/5
                      </td>
                      <td className="px-3 py-2 max-w-[280px] whitespace-pre-wrap break-words text-gray-700">{fb.review_text}</td>
                      <td className="px-3 py-2 max-w-[240px] whitespace-pre-wrap break-words text-xs text-gray-600">{fb.goals_snapshot || '-'}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          fb.status === 'approved'
                            ? 'bg-green-100 text-green-700'
                            : fb.status === 'rejected'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {fb.status}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => openConfirm({
                              title: 'Одобрить отзыв?',
                              description: 'После одобрения отзыв попадет в публичный раздел отзывов.',
                              actionType: 'feedback_approve',
                              payload: { feedbackId: fb.id },
                            })}
                            className="px-2 py-1 rounded-md text-xs bg-green-100 text-green-700 hover:bg-green-200"
                          >
                            Approve
                          </button>
                          <button
                            type="button"
                            onClick={() => openConfirm({
                              title: 'Отклонить отзыв?',
                              description: 'Отзыв не будет опубликован публично.',
                              actionType: 'feedback_reject',
                              payload: { feedbackId: fb.id },
                            })}
                            className="px-2 py-1 rounded-md text-xs bg-red-100 text-red-700 hover:bg-red-200"
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
      )}

      </div>

      {showUserProfileModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowUserProfileModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-5 flex items-center justify-between">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Профиль атлета</h3>
              <button type="button" onClick={() => setShowUserProfileModal(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-5 space-y-4">
              {userProfileLoading || !selectedAdminUser ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4897d2]" />
                </div>
              ) : (
                <>
                  <div className="grid sm:grid-cols-2 gap-3 text-sm">
                    <div className="border rounded-lg p-3"><span className="text-gray-500">Имя:</span> <b>{`${selectedAdminUser.first_name || ''} ${selectedAdminUser.last_name || ''}`.trim() || selectedAdminUser.username}</b></div>
                    <div className="border rounded-lg p-3"><span className="text-gray-500">Username:</span> <b>@{selectedAdminUser.username}</b></div>
                    <div className="border rounded-lg p-3"><span className="text-gray-500">Email:</span> <b>{selectedAdminUser.email || '—'}</b></div>
                    <div className="border rounded-lg p-3"><span className="text-gray-500">Телефон:</span> <b>{selectedAdminUser.profile_data?.phone || selectedAdminUser.phone || '—'}</b></div>
                    <div className="border rounded-lg p-3"><span className="text-gray-500">Дата рождения:</span> <b>{selectedAdminUser.profile_data?.birthday || '—'}</b></div>
                    <div className="border rounded-lg p-3"><span className="text-gray-500">Адрес:</span> <b>{selectedAdminUser.profile_data?.location || '—'}</b></div>
                    <div className="border rounded-lg p-3"><span className="text-gray-500">Команда:</span> <b>{selectedAdminUser.profile_data?.team_group || '—'}</b></div>
                    <div className="border rounded-lg p-3"><span className="text-gray-500">Анкета здоровья:</span> <b>{selectedAdminUser.profile_data?.health_form_completed ? 'Заполнена' : 'Не заполнена'}</b></div>
                  </div>
                  <div className="border rounded-lg p-3 text-sm">
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-2">О себе</p>
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedAdminUser.profile_data?.bio || '—'}</p>
                  </div>
                  <div className="border rounded-lg p-3">
                    <p className="text-xs uppercase tracking-wide text-gray-500 mb-3">Файлы анкет</p>
                    {Array.isArray(selectedAdminUser.questionnaire_documents) && selectedAdminUser.questionnaire_documents.length > 0 ? (
                      <div className="space-y-2">
                        {selectedAdminUser.questionnaire_documents.map((doc) => (
                          <div key={`doc-${doc.id}`} className="flex items-center justify-between gap-3 border rounded-lg px-3 py-2 text-sm">
                            <div>
                              <p className="font-medium text-gray-900">{doc.title || doc.document_type}</p>
                              <p className="text-xs text-gray-500">{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleString('ru-RU') : ''}</p>
                            </div>
                            {doc.file_url ? (
                              <a
                                href={doc.file_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#4897d2] text-white hover:opacity-90"
                              >
                                <Download className="w-4 h-4" />
                                Скачать
                              </a>
                            ) : (
                              <span className="text-gray-400">Файл недоступен</span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500">Файлы анкет не найдены.</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showOrderModal && orderModalData && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowOrderModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-5 flex items-center justify-between">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Заказ #{orderModalData.id}</h3>
              <button type="button" onClick={() => setShowOrderModal(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-5 space-y-4">
              <div className="text-sm text-gray-700">
                <p><span className="font-medium">Email:</span> {orderModalData.user_email || '-'}</p>
                <p><span className="font-medium">Дата:</span> {new Date(orderModalData.created_at).toLocaleString('ru-RU')}</p>
                <p><span className="font-medium">Сумма:</span> {parseFloat(orderModalData.total_price || 0).toFixed(2)}₪</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-900 mb-2">Статус заказа</p>
                <select
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  value={orderModalStatus}
                  onChange={(e) => setOrderModalStatus(e.target.value)}
                >
                  {orderStatuses.map((item) => (
                    <option key={item.value} value={item.value}>{item.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-900 mb-2">Товары</p>
                <div className="space-y-2">
                  {(orderModalData.items || []).length > 0 ? (
                    (orderModalData.items || []).map((item) => (
                      <div key={item.id} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <p className="text-sm font-medium text-gray-900">{item.product?.name || item.product_name || 'Товар'}</p>
                        <p className="text-xs text-gray-600">{parseFloat(item.price || 0).toFixed(2)}₪ × {item.quantity}</p>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500">Нет данных по товарам.</div>
                  )}
                </div>
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 sm:p-5 flex gap-2">
              <button
                type="button"
                onClick={requestOrderStatusUpdate}
                className="px-4 py-2 rounded-lg bg-[#4897d2] text-white hover:opacity-90"
              >
                Сохранить статус
              </button>
              <button
                type="button"
                onClick={() => setShowOrderModal(false)}
                className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {showTrainingModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowTrainingModal(false)}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 sm:p-5 flex items-center justify-between">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                {editingTrainingId ? 'Изменить тренировку' : 'Новая тренировка'}
              </h3>
              <button type="button" onClick={() => setShowTrainingModal(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-5 space-y-3">
              <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
                <p className="text-sm font-semibold text-gray-900">Как это работает</p>
                <p className="mt-1 text-xs leading-5 text-gray-600">
                  По умолчанию создавайте тренировку в режиме "Каждую неделю" — тогда она сразу будет видна атлетам по выбранному дню.
                  Режим "Только конкретная неделя" нужен только для разовой замены или особого расписания.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                {trainingScheduleTypeOptions.map((option) => {
                  const isActive = trainingForm.schedule_type === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setTrainingForm((prev) => ({
                        ...prev,
                        schedule_type: option.value,
                        week_start: option.value === 'specific_week'
                          ? (prev.week_start || getCurrentWeekStartISO())
                          : prev.week_start,
                      }))}
                      className={`rounded-xl border p-3 text-left transition-colors ${
                        isActive ? 'border-[#4897d2] bg-blue-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <p className="text-sm font-semibold text-gray-900">{option.label}</p>
                      <p className="mt-1 text-xs leading-5 text-gray-500">{option.description}</p>
                    </button>
                  );
                })}
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <label className="text-sm text-gray-700">
                  День недели
                  <select
                    value={trainingForm.weekday}
                    onChange={(e) => setTrainingForm((prev) => ({ ...prev, weekday: e.target.value }))}
                    className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                  >
                    {weekDaysOrder.map((day) => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                </label>
                <label className="text-sm text-gray-700">
                  Порядок
                  <input type="number" value={trainingForm.sort_order} onChange={(e) => setTrainingForm((prev) => ({ ...prev, sort_order: e.target.value }))} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" />
                </label>
              </div>

              <div className="grid sm:grid-cols-3 gap-3">
                <label className="text-sm text-gray-700">
                  Время
                  <input type="text" value={trainingForm.time} onChange={(e) => setTrainingForm((prev) => ({ ...prev, time: e.target.value }))} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" />
                </label>
                <label className="text-sm text-gray-700">
                  Иконка (emoji)
                  <input type="text" value={trainingForm.icon} onChange={(e) => setTrainingForm((prev) => ({ ...prev, icon: e.target.value }))} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" />
                </label>
                {trainingForm.schedule_type === 'specific_week' ? (
                  <label className="text-sm text-gray-700">
                    Начало недели
                    <input
                      type="date"
                      value={trainingForm.week_start}
                      onChange={(e) => setTrainingForm((prev) => ({ ...prev, week_start: e.target.value }))}
                      className="mt-1 w-full px-3 py-2 border rounded-lg text-sm"
                    />
                  </label>
                ) : (
                  <div className="text-xs text-gray-500 flex items-end pb-2">
                    Эта тренировка будет показываться атлетам каждую неделю по выбранному дню.
                  </div>
                )}
              </div>

              {trainingForm.schedule_type === 'specific_week' && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs leading-5 text-amber-900">
                  Такая тренировка видна только для выбранной недели. Если вы хотите постоянное расписание, переключите режим на "Каждую неделю".
                </div>
              )}

              <label className="text-sm text-gray-700 block">
                Заголовок тренировки
                <input type="text" value={trainingForm.title} onChange={(e) => setTrainingForm((prev) => ({ ...prev, title: e.target.value }))} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" />
              </label>

              <label className="text-sm text-gray-700 block">
                Локация
                <input type="text" value={trainingForm.location} onChange={(e) => setTrainingForm((prev) => ({ ...prev, location: e.target.value }))} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" />
              </label>

              <label className="text-sm text-gray-700 block">
                Доп. описание
                <textarea value={trainingForm.details} onChange={(e) => setTrainingForm((prev) => ({ ...prev, details: e.target.value }))} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" rows={2} />
              </label>

              <label className="text-sm text-gray-700 block">
                Примечание
                <textarea value={trainingForm.note} onChange={(e) => setTrainingForm((prev) => ({ ...prev, note: e.target.value }))} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" rows={2} />
              </label>

              <div className="grid sm:grid-cols-2 gap-3">
                <label className="text-sm text-gray-700">
                  Подпись ссылки
                  <input type="text" value={trainingForm.meeting_point_label} onChange={(e) => setTrainingForm((prev) => ({ ...prev, meeting_point_label: e.target.value }))} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" />
                </label>
                <label className="text-sm text-gray-700">
                  Ссылка
                  <input type="url" value={trainingForm.meeting_point_url} onChange={(e) => setTrainingForm((prev) => ({ ...prev, meeting_point_url: e.target.value }))} className="mt-1 w-full px-3 py-2 border rounded-lg text-sm" />
                </label>
              </div>

              <div className="flex items-center gap-6 pt-1">
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={trainingForm.is_new} onChange={(e) => setTrainingForm((prev) => ({ ...prev, is_new: e.target.checked }))} />
                  Пометка NEW
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                  <input type="checkbox" checked={trainingForm.is_published} onChange={(e) => setTrainingForm((prev) => ({ ...prev, is_published: e.target.checked }))} />
                  Опубликовано
                </label>
              </div>
            </div>
            <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 p-4 sm:p-5 flex gap-2">
              <button type="button" onClick={requestSaveTraining} className="px-4 py-2 rounded-lg bg-[#4897d2] text-white hover:opacity-90">
                {editingTrainingId ? 'Сохранить' : 'Добавить'}
              </button>
              <button type="button" onClick={() => setShowTrainingModal(false)} className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmModal.isOpen && (
        <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={closeConfirm}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-lg font-bold text-gray-900">{confirmModal.title}</h4>
            <p className="text-sm text-gray-600 mt-2">{confirmModal.description}</p>
            <div className="mt-5 flex gap-2">
              <button type="button" disabled={actionProcessing} onClick={confirmAction} className="px-4 py-2 rounded-lg bg-[#4897d2] text-white hover:opacity-90 disabled:opacity-60">
                Подтвердить
              </button>
              <button type="button" onClick={closeConfirm} className="px-4 py-2 rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200">
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}

      <ParticipantsModal
        isOpen={participantsModalData.isOpen}
        onClose={() => setParticipantsModalData({ isOpen: false, title: '', participants: [], totalCount: 0 })}
        title={participantsModalData.title}
        participants={participantsModalData.participants}
        totalCount={participantsModalData.totalCount}
      />
    </div>
  );
};

export default AdminDashboardTab;
